import React, { useCallback, useState } from "react";
import { Grid, Box, Modal } from "@material-ui/core";
import { CompareArrows as CompareArrowsIcon } from '@mui/icons-material';
import { useDispatch, useSelector } from "react-redux";
import QCXButton from "../../shared-components/button/QCXButton";
import QCXDataGridControl from "../../shared-components/data-grid-control/QCXDataGridControl";
import QCXFormTitle from "../form-title/QCXFormTitle"
import QCXFinalAlternativeCurrencyField from "../../shared-components/final-currency-field/QCXFinalAlternativeCurrencyField";
import QCXFormStepWizard from "../../shared-components/form-step-wizard/QCXFormStepWizard";
import { forceUnnormalizeNumeral, normalizeNumeral } from "../../utils/general/general-utils";
import { formatBrazilianNumericDecimal } from "../../utils/hooks/form/field/formatters";
import { selectRelatedSelectionItemList } from "../../features/danfe/danfeSelectors";
import { setRelatedSelectionItemList } from "../../features/danfe/danfeSlice";

export default function QCXDanfeComplementarAdicoesForm({
  isCreate,
  isConsult,
  isUpdate,
  model,
  handleSubmit,
  handleChangeToUpdate,
  handleCancelUpdate,
  controlComponentProps,
  ...restProps
}) {
  const dispatch = useDispatch();
  const complementarItemsList = useSelector(selectRelatedSelectionItemList);

  const adicoes = model?.adicoes ?? [];

  const [modalAdicaoId, setModalAdicaoId] = useState(null);

  const closeModal = () => setModalAdicaoId(null);

  const handleAlterarValoresClick = (form, rowId) => {
    setModalAdicaoId(rowId);

    const selectedAdicaoIndex = adicoes.findIndex(ad => ad.id === rowId);
    if (selectedAdicaoIndex === -1) return;

    const selectedAdicao = form.getState().values.adicoes[selectedAdicaoIndex];
    form.change("ignorableFields.adicaoIndex", selectedAdicaoIndex);
    form.change("ignorableFields.baseCalculoIcmsReal", forceUnnormalizeNumeral(selectedAdicao?.baseCalculoIcmsReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorIcmsReal", forceUnnormalizeNumeral(selectedAdicao?.valorIcmsReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorIiReal", forceUnnormalizeNumeral(selectedAdicao?.valorIiReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorIpiReal", forceUnnormalizeNumeral(selectedAdicao?.valorIpiReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorPisReal", forceUnnormalizeNumeral(selectedAdicao?.valorPisReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorCofinsReal", forceUnnormalizeNumeral(selectedAdicao?.valorCofinsReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.despesasAduaneiras", forceUnnormalizeNumeral(selectedAdicao?.despesasAduaneiras ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorSeguroReal", forceUnnormalizeNumeral(selectedAdicao?.valorSeguroReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorFreteReal", forceUnnormalizeNumeral(selectedAdicao?.valorFreteReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorAcrescimos", forceUnnormalizeNumeral(selectedAdicao?.valorAcrescimos ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorDeducoes", forceUnnormalizeNumeral(selectedAdicao?.valorDeducoes ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.valorTotalReal", forceUnnormalizeNumeral(selectedAdicao?.valorTotalReal ?? 0, formatBrazilianNumericDecimal(2)));
    form.change("ignorableFields.quantidade", forceUnnormalizeNumeral(selectedAdicao?.quantidade ?? 0, formatBrazilianNumericDecimal(2)));
  }

  const handleAlterarValoresSubmit = (form) => {
    const formValues = form.getState().values;
    const basePath = `adicoes[${formValues.ignorableFields.adicaoIndex}]`;
    form.change(`${basePath}.baseCalculoIcmsReal`, normalizeNumeral(formValues.ignorableFields.baseCalculoIcmsReal));
    form.change(`${basePath}.valorIcmsReal`, normalizeNumeral(formValues.ignorableFields.valorIcmsReal));
    form.change(`${basePath}.valorIiReal`, normalizeNumeral(formValues.ignorableFields.valorIiReal));
    form.change(`${basePath}.valorIpiReal`, normalizeNumeral(formValues.ignorableFields.valorIpiReal));
    form.change(`${basePath}.valorPisReal`, normalizeNumeral(formValues.ignorableFields.valorPisReal));
    form.change(`${basePath}.valorCofinsReal`, normalizeNumeral(formValues.ignorableFields.valorCofinsReal));
    form.change(`${basePath}.despesasAduaneiras`, normalizeNumeral(formValues.ignorableFields.despesasAduaneiras));
    form.change(`${basePath}.valorSeguroReal`, normalizeNumeral(formValues.ignorableFields.valorSeguroReal));
    form.change(`${basePath}.valorFreteReal`, normalizeNumeral(formValues.ignorableFields.valorFreteReal));
    form.change(`${basePath}.valorAcrescimos`, normalizeNumeral(formValues.ignorableFields.valorAcrescimos));
    form.change(`${basePath}.valorDeducoes`, normalizeNumeral(formValues.ignorableFields.valorDeducoes));
    form.change(`${basePath}.valorTotalReal`, normalizeNumeral(formValues.ignorableFields.valorTotalReal));
    form.change(`${basePath}.quantidade`, normalizeNumeral(formValues.ignorableFields.quantidade));

    closeModal();
  }

  const getColumns = useCallback((form) => ([
    {
      field: 'numeroAdicao',
      headerName: 'Adição',
      flex: 1,
    },
    {
      field: 'numeroSeqAdicao',
      headerName: 'Item',
      flex: 1,
    },
    {
      field: 'partnumber',
      headerName: 'Partnumber',
      flex: 1,
      valueGetter: ({ row }) => row?.mercadoria?.partnumber,
    },
    {
      field: 'exportador',
      headerName: 'Exportador',
      flex: 1,
      valueGetter: ({ row }) => row?.mercadoria?.exportador?.pessoaJuridica?.nomeResumido,
    },
    {
      field: 'fabricante',
      headerName: 'Fabricante',
      flex: 1,
      valueGetter: ({ row }) => row?.mercadoria?.fabricante?.pessoaJuridica?.nomeResumido,
    },
    {
      field: 'ncm',
      headerName: 'NCM',
      flex: 1,
      valueGetter: ({ row }) => row?.mercadoria?.ncm?.code,
    },
    {
      field: 'quantidade',
      headerName: 'Quantidade',
      flex: 1,
      valueGetter: ({ row }) => row?.quantidade,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      flex: 1,
      renderCell: ({ row }) => (
        <>
          <QCXButton
            name="btn-change-values"
            variant="text"
            color="secondary"
            size="small"
            tooltipDescription='Alterar valores'
            tooltip
            onClick={() => handleAlterarValoresClick(form, row.id)}
            disabled={!complementarItemsList?.includes(row.id)}
          >
            <CompareArrowsIcon />
          </QCXButton>
        </>
      ),
    },
  ]), [complementarItemsList]);

  return (
    <QCXFormStepWizard
      isCreate={isCreate}
      isConsult={isConsult}
      isUpdate={isUpdate}
      disablePristine
      initialValues={model}
      handleSubmitValues={handleSubmit}
      handleChangeToUpdate={handleChangeToUpdate}
      handleCancelUpdate={handleCancelUpdate}
      onSubmit={handleSubmit}
      controlComponentProps={controlComponentProps}
      {...restProps}
    >
      {({ form }) => (
        <>
          <QCXDataGridControl
            rows={adicoes}
            columns={getColumns(form)}
            checkboxSelection
            selectionModel={complementarItemsList}
            onSelectionModelChange={newSelection => dispatch(setRelatedSelectionItemList(newSelection?.selectionModel ?? []))}
          />

          <Modal open={modalAdicaoId} onClose={closeModal}>
            <div style={{
              background: '#fff',
              padding: 20,
              outline: 'none',
              borderRadius: 4,
              width: "80%%",
              maxWidth: 1000,
              margin: '100px auto',
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <QCXFormTitle title="Alterar valores" />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="base-calculo-icms-real"
                    name="ignorableFields.baseCalculoIcmsReal"
                    label="Base de cálculo do ICMS em Reais"
                  />
                </Grid>
                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-icms-real"
                    name="ignorableFields.valorIcmsReal"
                    label="Valor do ICMS em Reais"
                  />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-ii-real"
                    name="ignorableFields.valorIiReal"
                    label="Valor do II em Reais"
                  />
                </Grid>
                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-ipi-real"
                    name="ignorableFields.valorIpiReal"
                    label="Valor do IPI em Reais"
                  />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-pis-real"
                    name="ignorableFields.valorPisReal"
                    label="Valor do PIS em Reais"
                  />
                </Grid>
                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-cofins-real"
                    name="ignorableFields.valorCofinsReal"
                    label="Valor do COFINS em Reais"
                  />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-acrescimos"
                    name="ignorableFields.valorAcrescimos"
                    label="Valor dos acréscimos em Reais"
                  />
                </Grid>
                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-deducoes"
                    name="ignorableFields.valorDeducoes"
                    label="Valor das deduções em Reais"
                  />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-seguro-real"
                    name="ignorableFields.valorSeguroReal"
                    label="Valor do seguro em Reais"
                  />
                </Grid>
                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-frete-real"
                    name="ignorableFields.valorFreteReal"
                    label="Valor do frete em Reais"
                  />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-despesas-aduaneiras-real"
                    name="ignorableFields.despesasAduaneiras"
                    label="Valor das despesas aduaneiras em Reais"
                  />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="quantidade"
                    name="ignorableFields.quantidade"
                    label="Quantidade"
                    scale={2}
                  />
                </Grid>

                <Grid item xs={6}>
                  <QCXFinalAlternativeCurrencyField
                    id="valor-total-real"
                    name="ignorableFields.valorTotalReal"
                    label="Valor do produto em Reais"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box width="100%" display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 16 }}>
                    <QCXButton variant="outlined" color="primary" onClick={closeModal}>Cancelar</QCXButton>
                    <QCXButton variant="contained" color="secondary" onClick={() => handleAlterarValoresSubmit(form)}>Salvar</QCXButton>
                  </Box>
                </Grid>
              </Grid>
            </div>
          </Modal>
        </>

      )}
    </QCXFormStepWizard>
  )
}
